(() => {
  if (customElements.get('modern-header')) return;

  const hoverQuery = '(hover: hover) and (pointer: fine)';
  const reducedMotionQuery = '(prefers-reduced-motion: reduce)';
  const maxMenuColumns = 4;

  class ModernHeader extends HTMLElement {
    connectedCallback() {
      this.eventController?.abort();
      this.closeTimers?.forEach(clearTimeout);
      this.eventController = new AbortController();
      this.closeTimers = new Map();
      this.hoverSuppressed = new WeakSet();
      this.hoverPreviewsSuppressed = false;
      this.disclosures = Array.from(this.querySelectorAll('header-menu > details'));
      this.headerSurface = this.querySelector('.modern-header__header');
      this.scrim = this.querySelector('[data-modern-header-scrim]');
      this.hoverMedia = window.matchMedia(hoverQuery);
      this.reducedMotionMedia = window.matchMedia(reducedMotionQuery);
      this.layoutViewportWidth = this.getLayoutViewportWidth();

      const listenerOptions = { signal: this.eventController.signal };

      this.disclosures.forEach((details) => {
        const summary = details.querySelector('summary');
        const trigger = this.getDisclosureTrigger(details);
        trigger.addEventListener('mouseenter', this.handleDisclosureEnter, listenerOptions);
        trigger.addEventListener('mouseleave', this.handleDisclosureLeave, listenerOptions);
        summary?.addEventListener('click', this.handleSummaryClick, listenerOptions);
        details.addEventListener('toggle', this.handleToggle, listenerOptions);
        this.syncExpanded(details);
      });

      this.addEventListener('focusout', this.handleFocusOut, { ...listenerOptions, capture: true });
      this.addEventListener('pointerdown', this.handlePointerDown, listenerOptions);
      this.headerSurface?.addEventListener('mouseleave', this.handleHeaderLeave, listenerOptions);
      document.addEventListener('keydown', this.handleKeyDown, listenerOptions);
      document.addEventListener('keyup', this.handleKeyUp, { ...listenerOptions, capture: true });
      document.addEventListener('click', this.handleDocumentClick, listenerOptions);
      window.addEventListener('resize', this.handleResize, listenerOptions);

      const openDetails = this.disclosures.find((details) => details.open);
      if (openDetails) this.open(openDetails);
    }

    disconnectedCallback() {
      this.eventController?.abort();
      this.closeTimers?.forEach(clearTimeout);
      this.closeTimers?.clear();
      this.eventController = undefined;
    }

    handleDisclosureEnter = (event) => {
      const details = event.currentTarget.querySelector?.(':scope > details') ?? event.currentTarget;
      if (!this.hoverMedia.matches || this.hoverPreviewsSuppressed || this.hoverSuppressed.has(details)) return;
      details.setAttribute('data-open-origin', 'pointer');
      this.open(details);
    };

    handleDisclosureLeave = (event) => {
      const details = event.currentTarget.querySelector?.(':scope > details') ?? event.currentTarget;
      this.hoverSuppressed.delete(details);
    };

    handleSummaryClick = (event) => {
      const details = event.currentTarget.closest('details');
      this.hoverPreviewsSuppressed = false;
      if (!details?.open) return;

      event.preventDefault();
      if (details.hasAttribute('data-closing')) this.open(details);
      else {
        this.hoverPreviewsSuppressed = details.getAttribute('data-open-origin') === 'pointer';
        this.close(details);
      }
    };

    handleHeaderLeave = () => {
      this.hoverPreviewsSuppressed = false;
    };

    handlePointerDown = (event) => {
      event.target.closest('summary')?.closest('details')?.setAttribute('data-open-origin', 'pointer');
    };

    handleToggle = (event) => {
      const details = event.currentTarget;
      this.syncExpanded(details);

      if (details.open && !details.hasAttribute('data-closing')) {
        this.open(details);
      } else if (!details.open) {
        this.cancelClose(details);
        if (this.activeDetails === details) this.activeDetails = undefined;
        this.updateScrim();
      }
    };

    handleFocusOut = (event) => {
      const details = event.target.closest('header-menu > details');
      if (!details) return;
      event.stopPropagation();

      setTimeout(() => {
        if (this.isConnected && details.open && !details.contains(document.activeElement)) {
          this.close(details);
        }
      });
    };

    handleKeyDown = (event) => {
      if (event.isComposing) return;

      const summary = event.target instanceof Element ? event.target.closest('summary') : null;
      if ((event.key === 'Enter' || event.key === ' ') && summary && this.contains(summary)) {
        summary.closest('details')?.setAttribute('data-open-origin', 'keyboard');
      }

      if (event.key !== 'Escape') return;

      const details = this.activeDetails ?? this.disclosures.find((disclosure) => disclosure.open);
      if (!details) return;

      event.preventDefault();
      event.stopPropagation();
      this.suppressEscapeKeyUp = true;
      this.hoverSuppressed.add(details);
      const activeSummary = details.querySelector('summary');
      this.close(details, { immediate: true });
      activeSummary?.focus();
    };

    handleKeyUp = (event) => {
      if (event.key !== 'Escape' || !this.suppressEscapeKeyUp) return;
      event.preventDefault();
      event.stopPropagation();
      this.suppressEscapeKeyUp = false;
    };

    handleDocumentClick = (event) => {
      if (!(event.target instanceof Element) || !this.activeDetails) return;
      const menuPanel = this.activeDetails.querySelector('.mega-menu__content');
      if (this.headerSurface?.contains(event.target) || menuPanel?.contains(event.target)) return;

      this.close(this.activeDetails);
    };

    handleResize = () => {
      const viewportWidth = this.getLayoutViewportWidth();
      if (!viewportWidth || viewportWidth === this.layoutViewportWidth) return;

      this.layoutViewportWidth = viewportWidth;
      this.disclosures.filter((details) => details.open).forEach((details) => this.updateMenuGeometry(details));
    };

    open(details) {
      if (!details) return;

      this.disclosures.forEach((sibling) => {
        if (sibling !== details) this.close(sibling);
      });

      this.updateMenuGeometry(details);
      this.cancelClose(details);
      details.setAttribute('open', '');
      this.syncExpanded(details);
      this.activeDetails = details;
      this.updateScrim();
    }

    close(details, { immediate = false } = {}) {
      if (!details) return;

      if (!details.open) {
        this.finishClose(details);
        return;
      }

      if (
        immediate ||
        details.getAttribute('data-open-origin') === 'keyboard' ||
        this.reducedMotionMedia.matches
      ) {
        this.finishClose(details);
        return;
      }

      if (details.hasAttribute('data-closing')) return;

      details.setAttribute('data-closing', '');
      this.syncExpanded(details);

      if (this.activeDetails === details) this.activeDetails = undefined;
      this.updateScrim();

      const timer = setTimeout(() => this.finishClose(details, timer), this.closeDuration);
      this.closeTimers.set(details, timer);
    }

    finishClose(details, timer) {
      if (timer !== undefined && this.closeTimers.get(details) !== timer) return;

      this.cancelClose(details);
      details.removeAttribute('open');
      this.syncExpanded(details);
      if (this.activeDetails === details) this.activeDetails = undefined;
      this.updateScrim();
    }

    cancelClose(details) {
      const timer = this.closeTimers.get(details);
      if (timer !== undefined) clearTimeout(timer);
      this.closeTimers.delete(details);
      details.removeAttribute('data-closing');
    }

    updateScrim() {
      this.scrim?.toggleAttribute('data-active', this.disclosures.some((details) => details.open));
    }

    updateMenuGeometry(details) {
      const list = details.querySelector('.mega-menu__list');
      const viewportWidth = this.getLayoutViewportWidth();
      if (!list || !viewportWidth) return;

      const rect = this.getDisclosureTrigger(details).getBoundingClientRect();
      const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
      const gutter = rootFontSize * 2;
      const triggerCenter = rect.left + rect.width / 2;
      let alignment = 'center';
      let edgeOffset = 0;
      let availableWidth = 2 * Math.min(triggerCenter - gutter, viewportWidth - gutter - triggerCenter);

      if (triggerCenter < viewportWidth / 3) {
        alignment = 'start';
        const panelLeft = Math.max(gutter, rect.left);
        edgeOffset = panelLeft - rect.left;
        availableWidth = viewportWidth - gutter - panelLeft;
      } else if (triggerCenter > viewportWidth * 2 / 3) {
        alignment = 'end';
        const panelRight = Math.min(viewportWidth - gutter, rect.right);
        edgeOffset = rect.right - panelRight;
        availableWidth = panelRight - gutter;
      }

      availableWidth = Math.max(0, Math.floor(availableWidth));
      const itemCount = Math.max(1, list.children.length);
      const minimumColumnWidth = rootFontSize * 18;
      const columnGap = rootFontSize * 4;
      const panelPadding = rootFontSize * 10;
      let columns = Math.min(maxMenuColumns, itemCount);

      while (columns > 1 && panelPadding + columns * minimumColumnWidth + (columns - 1) * columnGap > availableWidth) {
        columns -= 1;
      }

      details.dataset.menuAlignment = alignment;
      details.style.setProperty('--modern-header-menu-edge-offset', `${edgeOffset}px`);
      details.style.setProperty('--modern-header-menu-available-inline-size', `${availableWidth}px`);
      details.style.setProperty('--modern-header-menu-columns', String(columns));
    }

    getLayoutViewportWidth() {
      return document.documentElement.clientWidth;
    }

    getDisclosureTrigger(details) {
      return details.closest('header-menu') ?? details;
    }

    get closeDuration() {
      const value = getComputedStyle(this).getPropertyValue('--modern-header-reveal-duration').trim();
      if (value.endsWith('ms')) return Number.parseFloat(value);
      if (value.endsWith('s')) return Number.parseFloat(value) * 1000;
      return 200;
    }

    syncExpanded(details) {
      details.querySelector('summary')?.setAttribute('aria-expanded', String(details.open));
    }
  }

  customElements.define('modern-header', ModernHeader);
})();
